import { useMemo, useState } from "react";
import Papa from "papaparse";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Download, CheckCircle, XCircle, MinusCircle, FileCsv, Info } from "@phosphor-icons/react";

const TEMPLATE = `manufacturer_name,series_name,description
3M,2080,Cast gloss & matte colour-change film
Avery Dennison,SW900,Supreme Wrapping Film
Hexis,Skintac,Cast polymeric wrapping film`;

function parseCSV(text) {
  const cleaned = text.replace(/^\ufeff/, "");
  const result = Papa.parse(cleaned, { header: true, skipEmptyLines: "greedy", transformHeader: (h) => h.trim() });
  const headers = (result.meta.fields || []).map((h) => h.trim());
  const rows = (result.data || []).map((r) => {
    const o = {};
    for (const h of headers) o[h] = (r[h] ?? "").toString().trim();
    return o;
  });
  return { headers, rows };
}

export default function SeriesImportDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [autoCreate, setAutoCreate] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => {
    if (!csvText.trim()) return { headers: [], rows: [], valid: false, error: null };
    try {
      const { headers, rows } = parseCSV(csvText);
      const h = headers.map((s) => s.toLowerCase());
      const valid = h.includes("manufacturer_name") && h.includes("series_name");
      return { headers, rows, valid, error: valid ? null : "CSV must include columns: manufacturer_name, series_name (description optional)." };
    } catch (e) { return { headers: [], rows: [], valid: false, error: String(e) }; }
  }, [csvText]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvText(await file.text());
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "series-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async () => {
    setSubmitting(true);
    try {
      const rows = parsed.rows.map((r) => ({
        manufacturer_name: r.manufacturer_name || "",
        series_name: r.series_name || "",
        description: r.description || "",
      }));
      const { data } = await api.post("/series/bulk", {
        rows, auto_create_missing: autoCreate, update_existing: updateExisting,
      });
      setResult(data);
      toast.success(`Imported: ${data.created_count} created, ${data.updated_count || 0} updated`);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const reset = () => { setCsvText(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-md" data-testid="series-bulk-import-btn">
          <UploadSimple size={16} /> Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileCsv size={20} /> Bulk Series Import</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono tracking-[0.15em] uppercase text-slate-500">Template:</span>
              <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-2 rounded-md text-xs h-8" data-testid="download-series-template">
                <Download size={12} /> Series CSV
              </Button>
            </div>

            <Tabs defaultValue="upload">
              <TabsList>
                <TabsTrigger value="upload" data-testid="series-bulk-tab-upload">Upload File</TabsTrigger>
                <TabsTrigger value="paste" data-testid="series-bulk-tab-paste">Paste CSV</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Upload .csv file</Label>
                <input type="file" accept=".csv,text/csv" onChange={onFile}
                  className="block w-full text-sm border border-slate-200 rounded-md p-2 file:mr-4 file:py-2 file:px-3 file:border-0 file:text-xs file:font-medium file:bg-slate-100 hover:file:bg-slate-200"
                  data-testid="series-bulk-file-input" />
              </TabsContent>
              <TabsContent value="paste" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Paste CSV (with header row)</Label>
                <Textarea rows={7} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={TEMPLATE} className="font-mono text-xs" data-testid="series-bulk-csv-textarea" />
              </TabsContent>
            </Tabs>

            {parsed.error && (
              <div className="border border-red-200 bg-red-50 text-red-700 rounded-md p-3 text-sm">{parsed.error}</div>
            )}

            {parsed.valid && (
              <div className="border border-slate-200 bg-slate-50 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Rows detected</div>
                    <div className="font-heading text-base font-semibold mt-0.5">{parsed.rows.length} series</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                    <div className="pr-2">
                      <div className="text-xs font-medium">Auto-create manufacturers</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Create a brand if it doesn't exist yet</div>
                    </div>
                    <Switch checked={autoCreate} onCheckedChange={setAutoCreate} data-testid="series-auto-create-switch" />
                  </div>
                  <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                    <div className="pr-2">
                      <div className="text-xs font-medium">Update existing</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Update the description instead of skipping</div>
                    </div>
                    <Switch checked={updateExisting} onCheckedChange={setUpdateExisting} data-testid="series-update-existing-switch" />
                  </div>
                </div>
              </div>
            )}

            {parsed.valid && parsed.rows.length > 0 && (
              <div className="border border-slate-200 rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex justify-between">
                  <span>Preview</span>
                  <span>showing first 5 of {parsed.rows.length}</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left"><th className="px-2 py-1.5">Manufacturer</th><th className="px-2 py-1.5">Series</th><th className="px-2 py-1.5">Description</th></tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5">{r.manufacturer_name}</td>
                        <td className="px-2 py-1.5 font-medium">{r.series_name}</td>
                        <td className="px-2 py-1.5 text-slate-500">{r.description || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-4 gap-0 border border-slate-200">
              <Stat icon={<CheckCircle weight="fill" size={18} className="text-emerald-600" />} label="Created" value={result.created_count} accent="text-emerald-700" />
              <Stat icon={<CheckCircle weight="fill" size={18} className="text-blue-600" />} label="Updated" value={result.updated_count || 0} accent="text-blue-700" />
              <Stat icon={<MinusCircle weight="fill" size={18} className="text-slate-500" />} label="Skipped" value={result.skipped_count} />
              <Stat icon={<XCircle weight="fill" size={18} className="text-red-600" />} label="Errors" value={result.error_count} accent="text-red-600" last />
            </div>
            <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <span>Only the catalogue of series was changed — your products and stock were not touched.</span>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Errors</div>
                <div className="border border-red-200 rounded-md max-h-48 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="px-3 py-2 border-b last:border-0 border-red-100 text-xs">
                      <span className="font-mono text-slate-500">Row {e.row}:</span> <span className="text-red-700">{e.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={onImport} disabled={submitting || !parsed.valid || parsed.rows.length === 0}
                className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="series-bulk-import-confirm">
                <UploadSimple size={16} /> {submitting ? "Importing…" : "Import"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} className="rounded-md" data-testid="series-import-another">Import another</Button>
              <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon, label, value, accent, last }) {
  return (
    <div className={`p-4 ${last ? "" : "border-r border-slate-200"}`}>
      <div className="flex items-center gap-2">{icon}</div>
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">{label}</div>
      <div className={`font-mono text-3xl font-semibold mt-1 ${accent || "text-slate-900"} tabular-nums`}>{value}</div>
    </div>
  );
}
